import { Observable } from 'rxjs';

/**
 * Type of breadcrumb item.
 * - 'link': Standard navigation link (default)
 * - 'dropdown': Interactive dropdown for selecting values (non-navigation)
 * - 'month-picker': Month navigation with prev/next controls
 * - 'date-picker': Unified date picker that adapts between week, month and single-day selection
 */
export type BreadcrumbItemType = 'link' | 'dropdown' | 'month-picker' | 'date-picker';

/**
 * Structured result from a label resolver that carries both label and entity appearance.
 * Return this from a BreadcrumbLabelResolver to display entity visuals in breadcrumbs.
 */
export interface BreadcrumbResolvedItem {
  label: string;
  icon?: string | null;
  color?: string | null;
  imageUrl?: string | null;
}

/**
 * Option for dropdown breadcrumb items.
 */
export interface BreadcrumbDropdownOption {
  /** Display label */
  label: string;
  /** Value to emit on selection */
  value: string;
  /** When true, renders with a top separator and action styling (e.g. "Create new...") */
  isAction?: boolean;
  /** Material icon name for entity visual */
  icon?: string | null;
  /** Background color for entity icon surface */
  color?: string | null;
  /** Image URL for entity avatar */
  imageUrl?: string | null;
}

/**
 * Represents a single breadcrumb item in the navigation trail.
 */
export interface BreadcrumbItem {
  /** Type of breadcrumb item. Defaults to 'link' if not specified. */
  type?: BreadcrumbItemType;

  /** Display label (can be a translation key). Optional for date-picker/month-picker types where label is computed from date. */
  label?: string;

  /** Route to navigate to (if clickable) - only for 'link' type */
  route?: string[];

  /** Query params to include in navigation - only for 'link' type */
  queryParams?: Record<string, string>;

  /** Whether this item has a dropdown for siblings - only for 'link' type */
  hasSiblings?: boolean;

  /** Function to load siblings (lazy-loaded on first click) - only for 'link' type */
  siblingsLoader?: () => Observable<BreadcrumbItem[]>;

  /** Icon to display before label */
  icon?: string;

  /** Background color for entity icon surface */
  color?: string | null;

  /** Image URL for entity avatar */
  imageUrl?: string | null;

  /** Whether this is the current (active) item */
  isCurrent?: boolean;

  /**
   * When true, this item renders as a non-interactive section label separator
   * inside the siblings dropdown. No route or click handler is applied.
   * Use `sectionLabel` to provide the display text.
   */
  separator?: boolean;

  /**
   * Text to display inside a separator item.
   * Only used when `separator: true`.
   */
  sectionLabel?: string;

  // --- Dropdown type properties ---

  /** Options for dropdown selection - only for 'dropdown' type */
  options?: BreadcrumbDropdownOption[];

  /** Currently selected value - only for 'dropdown' type */
  value?: string | null;

  /** Callback when selection changes - only for 'dropdown' type */
  onSelect?: (value: string | null) => void;

  /** Placeholder when no value selected - only for 'dropdown' type */
  placeholder?: string;

  // --- Month-picker type properties ---

  /** Current date for month picker - only for 'month-picker' type */
  date?: Date;

  /** Callback when date changes - only for 'month-picker' type */
  onDateChange?: (date: Date) => void;

  // --- Date-picker type properties ---

  /** Display mode for date picker - 'week' shows calendar with week rows, 'month' shows month grid,
   *  'day' shows calendar with single-day selection. Only for 'date-picker' type */
  datePickerMode?: 'week' | 'month' | 'day';

  /** Callback when a week is selected (for week mode) - only for 'date-picker' type */
  onWeekSelect?: (weekStart: Date) => void;

  /** Callback when a single day is selected (for day mode) - only for 'date-picker' type */
  onDaySelect?: (day: Date) => void;
}

/**
 * Configuration for breadcrumbs in route data.
 * Used to define how breadcrumbs should be generated for a route.
 */
export interface BreadcrumbConfig {
  /** Static label or translation key */
  label: string;

  /** Icon to display (should match navigation config icons) */
  icon?: string;

  /** Resolver name for dynamic labels (e.g., 'person' to resolve person name) */
  resolver?: string;

  /** Siblings loader identifier */
  siblingsLoader?: string;

  /** Whether this breadcrumb is inactive (no link, just text) */
  inactive?: boolean;

  /** Whether to skip this breadcrumb in the trail (used for index routes) */
  skip?: boolean;

  /** Parent breadcrumb config (recursive) */
  parent?: BreadcrumbConfig;
}

/**
 * Internal type for tracking truncated breadcrumb items.
 */
export interface BreadcrumbDisplayItem extends BreadcrumbItem {
  /** Whether this item is a truncation placeholder */
  isTruncated?: boolean;

  /** Original item index in the source breadcrumb list */
  sourceIndex?: number;
}
